import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import api from '../api/client';

/**
 * Background Upload Manager
 * =========================
 *
 * A global, page-independent uploader for the admin panel.
 *
 * Why this exists
 * ---------------
 * Previously, image uploads lived inside <ImageUpload/> and used the axios
 * instance directly. If the admin navigated to a different tab mid-upload,
 * React unmounted the component and the axios request was silently cancelled
 * (or its response was thrown away). This context runs the uploads OUTSIDE
 * any specific component so they survive route changes.
 *
 * How components use it
 * ---------------------
 *   const { upload } = useUploadManager();
 *   const { promise } = upload(file);
 *   const { url } = await promise;    // resolves with { url, filename, ... }
 *
 * The returned `promise` is safe to `await` inside a component effect —
 * if the component unmounts, the upload keeps running in the background and
 * the `<UploadTray/>` fed by this context shows progress / success / error.
 *
 * The provider also:
 *   * Wires a `beforeunload` handler so the browser warns the admin if they
 *     try to close/refresh the tab while uploads are pending. Internal SPA
 *     navigation (React Router) does NOT trigger this — only actual tab
 *     close / reload / off-domain navigation.
 *   * Fires a "Successfully saved" toast when each upload completes.
 */

const UploadManagerContext = createContext(null);

let _uploadIdSeq = 0;

export const UploadManagerProvider = ({ children }) => {
  const [uploads, setUploads] = useState([]);
  // We keep a ref mirror of `uploads` so the beforeunload handler always
  // sees the freshest state (event handlers close over stale state otherwise).
  const uploadsRef = useRef(uploads);
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);

  const patch = useCallback((id, next) => {
    setUploads(prev => prev.map(u => (u.id === id ? { ...u, ...next } : u)));
  }, []);

  const dismiss = useCallback((id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setUploads(prev => prev.filter(u => u.status === 'uploading' || u.status === 'queued'));
  }, []);

  /**
   * Start an upload. Returns { id, promise }.
   *
   * @param {File}   file
   * @param {object} [opts]
   * @param {string} [opts.endpoint='/admin/upload']  – backend endpoint (relative)
   * @param {string} [opts.label]                     – nice name shown in the tray
   * @param {string} [opts.kind='image']              – 'image' | 'document'
   */
  const upload = useCallback((file, opts = {}) => {
    const id = ++_uploadIdSeq;
    const label = opts.label || file.name || `upload-${id}`;
    setUploads(prev => [
      ...prev,
      { id, label, filename: file.name, size: file.size, progress: 0, status: 'uploading', kind: opts.kind || 'image' },
    ]);

    const endpoint = opts.endpoint || '/admin/upload';
    const fd = new FormData();
    fd.append('file', file);

    const promise = api.post(endpoint, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (!e.total) return;
        const progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
        patch(id, { progress });
      },
    }).then(res => {
      patch(id, { status: 'success', progress: 100, url: res.data?.url, filename: res.data?.filename, persistent: res.data?.persistent });
      toast.success(`Successfully saved: ${label}`);
      // Auto-dismiss the tray row after a brief delay so it doesn't pile up.
      setTimeout(() => dismiss(id), 5000);
      return res.data;
    }).catch(err => {
      const detail = err?.response?.data?.detail || err.message || 'Upload failed';
      patch(id, { status: 'error', error: detail });
      toast.error(`Upload failed: ${detail}`);
      throw err;
    });

    return { id, promise };
  }, [patch, dismiss]);

  // beforeunload guard — only when there's an active upload. Fires only for
  // full-page reload / tab close (browser-native); SPA navigation is fine.
  useEffect(() => {
    const handler = (e) => {
      const active = uploadsRef.current.some(u => u.status === 'uploading' || u.status === 'queued');
      if (!active) return;
      e.preventDefault();
      // Most browsers ignore the custom message and show their own generic
      // "Leave site?" prompt, but returnValue must be set for the prompt
      // to render in older Chrome versions.
      e.returnValue = 'You have uploads still in progress. Leaving will cancel them.';
      return e.returnValue;
    };
    const active = uploads.some(u => u.status === 'uploading' || u.status === 'queued');
    if (active) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploads]);

  const activeCount = uploads.filter(u => u.status === 'uploading' || u.status === 'queued').length;
  const hasActive = activeCount > 0;

  return (
    <UploadManagerContext.Provider value={{ upload, uploads, dismiss, clearFinished, activeCount, hasActive }}>
      {children}
    </UploadManagerContext.Provider>
  );
};

export const useUploadManager = () => useContext(UploadManagerContext);
